import type { Router } from 'vue-router'
import NProgress from 'nprogress'
import {
  Modal,
  message as AntMessage,
  notification as AntNotification,
  message,
} from 'ant-design-vue'
import { AxiosCanceler } from '@/utils/http/axiosCanceler'
import { notifyRouteChange } from '../routeChange'
import { useBaseStoreWithOut } from '@/store-pinia/modules/base'
import { useUserStoreWithOut } from '@/store-pinia/modules/user'
import { createLogoutGuard } from './logoutGuard'
import { createPermissionGuard } from './permissionGuard'
import { createParamsGuard } from './paramsMenuGuard'

// 初始化路由守卫
export function setupRouterGuard(router: Router) {
  createBasicGuard(router)
  createPageLoadingGuard(router)
  // createNProgressGuard(router)
  createMessageGuard(router)
  createHttpGuard(router)
  createPermissionGuard(router)
  createLogoutGuard(router)
  createParamsGuard(router)
}

/**
 * 创建路由是否已加载守护，记录每个路由加载完成状态
 * @param router
 */
export function createBasicGuard(router: Router) {
  const loadedPagesMap = new Map()

  router.beforeEach(async (to) => {
    to.meta.loaded = !!loadedPagesMap.get(to.path)
    notifyRouteChange(to)
    return true
  })

  router.afterEach(async (to) => {
    loadedPagesMap.set(to.path, true)
    return true
  })

  router.onError((error) => {
    if (error instanceof TypeError) {
      if (error.message.includes('imported module')) {
        setTimeout(() => {
          location.reload()
        }, 2000)
        message.error('There is a new version release system refresh')
      }
    }
  })
}

/**
 * 创建NProgress守护，路由切换的时候显示加载进度条
 * @param router
 */
export function createNProgressGuard(router: Router) {
  NProgress.configure({ showSpinner: false })
  router.beforeEach(async (to) => {
    if (to.meta.loaded) {
      return true
    }
    NProgress.start()
    return true
  })

  router.afterEach(async () => {
    NProgress.done()
    return true
  })
}

/**
 * 创建消息守护, 在路由切换之前关闭所有模态框，消息弹窗，通知框等。
 * @param router
 */
export function createMessageGuard(router: Router) {
  router.beforeEach(async () => {
    try {
      Modal.destroyAll()
      AntMessage.destroy()
      AntNotification.destroy()
    } catch (error) {
      console.error(error)
    }
    return true
  })
}

/**
 * 创建Axios请求守护，在路由切换之前取消所有axios请求。
 * @param router
 */
export function createHttpGuard(router: Router) {
  router.beforeEach(async () => {
    AxiosCanceler.removeAllPendings()
    return true
  })
}

/**
 * 创建页面加载守护，在路由页面加载中显示loading
 * @param router
 */
export function createPageLoadingGuard(router: Router) {
  const baseStore = useBaseStoreWithOut()
  const userStore = useUserStoreWithOut()

  router.beforeEach(async (to) => {
    if (!userStore.getToken) {
      return true
    }
    if (to.meta.loaded) {
      return true
    }

    baseStore.changePageLoading(true)
    return true
  })

  router.afterEach(async () => {
    // 防止过快产生闪烁
    setTimeout(() => {
      baseStore.setPageLoading(false)
    }, 200)
    return true
  })
}
